import React, { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, ChevronDown, ChevronUp } from "lucide-react";

const LearningSection: React.FC = () => {
  const [expandedModule, setExpandedModule] = useState<number | null>(null);

  const learningModules = [
    {
      title: "1. Generating Unlimited Leads & Mastering Sales",
      points: [
        "Fix common issues (mishandled appointments, confused customers, stagnant growth)",
        "Discover AI-powered tools to boost retention & fill calendars",
        "Leverage booking apps (Web, Android, iOS) to attract customers",
        "Run high-converting SMS, WhatsApp & Email campaigns",
        "Implement referral programs & upselling strategies"
      ]
    },
    {
      title: "2. Maximizing Customer Value & Retention",
      points: [
        "Use CRM to build detailed customer profiles",
        "Create loyalty programs that increase retention 4–5x",
        "Automate reminders to reduce no-shows & double bookings"
      ]
    },
    {
      title: "3. Optimizing Staff Management & Doubling Revenue",
      points: [
        "Manage staff with smart tools & increase engagement by 46%",
        "Use analytics to grow revenue by 30%",
        "Adopt cloud-based solutions to future-proof your business"
      ]
    }
  ];

  const toggleModule = (index: number) => {
    setExpandedModule(expandedModule === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-black">
              What You Will Learn in 3 Hours
            </h2>
            <div className="w-24 h-1 bg-red-500 mx-auto mb-8"></div>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          {/* Learning Modules - Full Width */}
          <div className="space-y-4">
          {learningModules.map((section, sectionIndex) => (
                <motion.div
                  key={sectionIndex}
                  initial={{ opacity: 0, y: 50, x: -30 }}
                  whileInView={{ opacity: 1, y: 0, x: 0 }}
                  transition={{ 
                    duration: 0.8, 
                    delay: sectionIndex * 0.2,
                    type: "spring",
                    stiffness: 80
                  }}
                  viewport={{ once: true }}
                  className="rounded-2xl shadow-lg border border-gray-200 overflow-hidden"
                  style={{ backgroundColor: '#f8f9fa' }}
                >
              <button
                onClick={() => toggleModule(sectionIndex)}
                className="w-full p-6 text-left flex items-center justify-between"
              >
                <h3 className="text-xl font-bold text-black pr-4">{section.title}</h3>
                <div className="flex-shrink-0">
                  {expandedModule === sectionIndex ? (
                    <ChevronUp className="h-6 w-6 text-red-400" />
                  ) : (
                    <ChevronDown className="h-6 w-6 text-red-400" />
                  )}
                </div>
              </button>
              
                  <motion.div
                    initial={false}
                    animate={{
                      height: expandedModule === sectionIndex ? "auto" : 0,
                      opacity: expandedModule === sectionIndex ? 1 : 0,
                      y: expandedModule === sectionIndex ? 0 : -20
                    }}
                    transition={{ 
                      duration: 0.5, 
                      ease: "easeInOut",
                      type: "spring",
                      stiffness: 100
                    }}
                    className="overflow-hidden"
                  >
                <div className="px-6 pb-6">
                  <ul className="space-y-3">
                    {section.points.map((point, pointIndex) => (
                      <li key={pointIndex} className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-red-400 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-700">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            </motion.div>
          ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LearningSection;
